import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop, { PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import {
    clampCropFraction,
    CROP_MIN_FRACTION,
    CROP_WARN_LONG_SIDE,
    CropFraction,
    cropPixelRect,
    DRAWING_RASTER_LONG_SIDE,
    fitLongSide,
    FULL_CROP,
    isFullCrop,
} from '../utils/pdf/blueprintSourceLogic';
import './BlueprintCropStep.css';

const ZOOM_LEVELS = [1, 1.5, 2, 3, 4, 6];

/** Share of the window height the drawing may take at 100 % zoom. */
const FIT_HEIGHT_SHARE = 0.55;

interface BlueprintCropStepProps {
    /** The drawing as it will be displayed (an image file, or a rendered PDF page). */
    previewUrl: string;
    crop: CropFraction;
    onChange: (crop: CropFraction) => void;
    /**
     * True for an image file: the preview is the file itself, so the resulting
     * pixel size is shown and a small crop is warned about. A PDF stays vector.
     */
    showPixelSize: boolean;
    disabled?: boolean;
}

const toPercentCrop = (crop: CropFraction): PercentCrop => ({
    unit: '%',
    x: crop.x * 100,
    y: crop.y * 100,
    width: crop.width * 100,
    height: crop.height * 100,
});

const BlueprintCropStep: React.FC<BlueprintCropStepProps> = ({ previewUrl, crop, onChange, showPixelSize, disabled }) => {
    const viewportRef = useRef<HTMLDivElement>(null);
    const [zoomIndex, setZoomIndex] = useState(0);
    const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
    const [fitWidth, setFitWidth] = useState<number | null>(null);

    // Width at which the whole drawing fits the viewport; zoom widens it and the viewport scrolls.
    const measure = useCallback(() => {
        const viewport = viewportRef.current;
        if (!viewport || !natural || !(viewport.clientWidth > 0)) {
            setFitWidth(null);
            return;
        }
        const maxHeight = window.innerHeight * FIT_HEIGHT_SHARE;
        setFitWidth(Math.floor(Math.min(viewport.clientWidth, maxHeight * (natural.width / natural.height))));
    }, [natural]);

    useEffect(() => {
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [measure]);

    useEffect(() => {
        setZoomIndex(0);
    }, [previewUrl]);

    const handleChange = (_: unknown, percent: PercentCrop) => {
        const next = { x: percent.x / 100, y: percent.y / 100, width: percent.width / 100, height: percent.height / 100 };
        // Below the minimum the rectangle simply stops following the pointer.
        if (next.width < CROP_MIN_FRACTION || next.height < CROP_MIN_FRACTION) return;
        onChange(clampCropFraction(next));
    };

    const zoom = ZOOM_LEVELS[zoomIndex];
    const full = isFullCrop(crop);
    const resultSize =
        showPixelSize && natural
            ? (() => {
                  const area = full ? natural : cropPixelRect(natural.width, natural.height, crop);
                  return fitLongSide(area.width, area.height, DRAWING_RASTER_LONG_SIDE);
              })()
            : null;
    const tooSmall = resultSize !== null && Math.max(resultSize.width, resultSize.height) < CROP_WARN_LONG_SIDE;

    return (
        <div className="blueprint-crop">
            <p className="blueprint-crop__hint">
                Dra i ramens kanter för att välja vilken del av ritningen som ska användas. Lämna ramen orörd för att
                använda hela.
            </p>
            <div className="blueprint-crop__toolbar">
                <button
                    type="button"
                    className="blueprint-crop__tool"
                    onClick={() => setZoomIndex(i => Math.max(0, i - 1))}
                    disabled={zoomIndex === 0}
                    aria-label="Zooma ut"
                >
                    <ZoomOut size={18} />
                </button>
                <span className="blueprint-crop__zoom" aria-live="polite">{Math.round(zoom * 100)} %</span>
                <button
                    type="button"
                    className="blueprint-crop__tool"
                    onClick={() => setZoomIndex(i => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
                    disabled={zoomIndex === ZOOM_LEVELS.length - 1}
                    aria-label="Zooma in"
                >
                    <ZoomIn size={18} />
                </button>
                <button
                    type="button"
                    className="blueprint-crop__tool blueprint-crop__reset"
                    onClick={() => onChange(FULL_CROP)}
                    disabled={full || disabled}
                >
                    <RotateCcw size={16} /> Återställ
                </button>
            </div>
            <div className="blueprint-crop__viewport" ref={viewportRef}>
                <ReactCrop
                    crop={toPercentCrop(crop)}
                    onChange={handleChange}
                    keepSelection
                    disabled={disabled}
                    className="blueprint-crop__area"
                    style={{ width: fitWidth ? `${Math.round(fitWidth * zoom)}px` : `${zoom * 100}%` }}
                >
                    <img
                        src={previewUrl}
                        alt="Förhandsvisning av ritningen"
                        className="blueprint-crop__image"
                        draggable={false}
                        onLoad={event =>
                            setNatural({
                                width: event.currentTarget.naturalWidth,
                                height: event.currentTarget.naturalHeight,
                            })
                        }
                    />
                </ReactCrop>
            </div>
            {resultSize && (
                <div className="blueprint-crop__size">
                    Ritningens storlek: {resultSize.width} × {resultSize.height} px
                </div>
            )}
            {tooSmall && (
                <div className="blueprint-crop__warning" role="status">
                    Den valda delen är liten och ritningen kan bli suddig. Använd en bild med högre upplösning eller en
                    PDF om det går.
                </div>
            )}
        </div>
    );
};

export default BlueprintCropStep;
